import React, { useState } from 'react';
import { 
  Receipt, 
  Download, 
  Eye, 
  Calendar, 
  DollarSign,
  User,
  CheckCircle,
  Clock,
  AlertCircle,
  Filter,
  Search,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../ui/avatar';

const PurchaseOrders = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock purchase orders data
  const purchaseOrders = [
    {
      id: 'PO-2024-001',
      trainer: {
        name: 'Dr. Sarah Johnson',
        avatar: null,
        specialization: 'Leadership Development'
      },
      trainingTitle: 'Executive Leadership Workshop',
      orderDate: '2024-09-20',
      trainingDate: '2024-10-02',
      amount: 18000,
      status: 'approved',
      participants: 25,
      duration: '3 days',
      location: 'Conference Room A, HQ',
      paymentTerms: 'Net 30',
      description: 'Comprehensive leadership development program for senior management team',
      milestones: [
        { name: 'Pre-training assessment', status: 'completed', date: '2024-09-25' },
        { name: 'Training delivery', status: 'pending', date: '2024-10-02' },
        { name: 'Post-training evaluation', status: 'pending', date: '2024-10-05' },
        { name: 'Final payment', status: 'pending', date: '2024-11-01' }
      ]
    },
    {
      id: 'PO-2024-002',
      trainer: {
        name: 'Michael Chen',
        avatar: null,
        specialization: 'Technical Training'
      },
      trainingTitle: 'Digital Transformation Training',
      orderDate: '2024-09-18',
      trainingDate: '2024-10-05',
      amount: 15500,
      status: 'pending',
      participants: 40,
      duration: '2 days',
      location: 'Virtual Session',
      paymentTerms: 'Net 15',
      description: 'Digital transformation strategies and implementation for IT teams',
      milestones: [
        { name: 'Contract signing', status: 'completed', date: '2024-09-18' },
        { name: 'Material preparation', status: 'in-progress', date: '2024-09-30' },
        { name: 'Training delivery', status: 'pending', date: '2024-10-05' },
        { name: 'Final payment', status: 'pending', date: '2024-10-20' }
      ]
    },
    {
      id: 'PO-2024-003',
      trainer: {
        name: 'Emily Rodriguez',
        avatar: null,
        specialization: 'Project Management'
      },
      trainingTitle: 'Project Management Fundamentals',
      orderDate: '2024-08-25',
      trainingDate: '2024-09-15',
      amount: 12000,
      status: 'completed',
      participants: 15,
      duration: '2 days',
      location: 'Training Center B',
      paymentTerms: 'Net 30',
      description: 'Foundation course in project management methodologies and best practices',
      milestones: [
        { name: 'Pre-training assessment', status: 'completed', date: '2024-09-10' },
        { name: 'Training delivery', status: 'completed', date: '2024-09-15' },
        { name: 'Post-training evaluation', status: 'completed', date: '2024-09-20' },
        { name: 'Final payment', status: 'completed', date: '2024-10-15' }
      ]
    },
    {
      id: 'PO-2024-004',
      trainer: {
        name: 'James Wilson',
        avatar: null,
        specialization: 'Team Development'
      },
      trainingTitle: 'Team Building Retreat',
      orderDate: '2024-09-22',
      trainingDate: '2024-10-08',
      amount: 8500,
      status: 'draft',
      participants: 20,
      duration: '1 day',
      location: 'Outdoor Activity Center',
      paymentTerms: 'Net 15',
      description: 'Interactive team building activities to improve collaboration and communication',
      milestones: [
        { name: 'Venue booking', status: 'pending', date: '2024-10-01' },
        { name: 'Activity planning', status: 'pending', date: '2024-10-05' },
        { name: 'Training delivery', status: 'pending', date: '2024-10-08' },
        { name: 'Final payment', status: 'pending', date: '2024-10-23' }
      ]
    },
    {
      id: 'PO-2024-005',
      trainer: {
        name: 'Lisa Zhang',
        avatar: null,
        specialization: 'Organizational Development'
      },
      trainingTitle: 'Change Management Workshop',
      orderDate: '2024-09-10',
      trainingDate: '2024-09-25',
      amount: 22000,
      status: 'cancelled',
      participants: 35,
      duration: '3 days',
      location: 'Main Auditorium',
      paymentTerms: 'Net 30',
      description: 'Change management strategies for organizational transformation',
      milestones: [
        { name: 'Initial planning', status: 'completed', date: '2024-09-10' },
        { name: 'Material development', status: 'cancelled', date: '2024-09-20' },
        { name: 'Training delivery', status: 'cancelled', date: '2024-09-25' },
        { name: 'Final payment', status: 'cancelled', date: '2024-10-25' }
      ]
    }
  ];

  const filteredOrders = purchaseOrders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.trainer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.trainingTitle.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || order.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <FileText className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <AlertCircle className="w-4 h-4" />;
      default: return <Receipt className="w-4 h-4" />;
    }
  };

  const getMilestoneStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in-progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleDownloadPO = (order) => {
    console.log('Download PO:', order.id);
    alert(`Downloading purchase order ${order.id}`);
  };

  const handleViewDetails = (order) => {
    console.log('View PO details:', order.id);
    alert(`Opening detailed view for ${order.id}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Receipt className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
            <p className="text-gray-600">Track and manage your training service purchase orders</p>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by PO number, trainer, or training title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Orders ({purchaseOrders.length})</option>
              <option value="draft">Draft ({purchaseOrders.filter(o => o.status === 'draft').length})</option>
              <option value="pending">Pending ({purchaseOrders.filter(o => o.status === 'pending').length})</option>
              <option value="approved">Approved ({purchaseOrders.filter(o => o.status === 'approved').length})</option>
              <option value="completed">Completed ({purchaseOrders.filter(o => o.status === 'completed').length})</option>
              <option value="cancelled">Cancelled ({purchaseOrders.filter(o => o.status === 'cancelled').length})</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Orders List */}
      <div className="space-y-4">
        {filteredOrders.map((order) => (
          <Card key={order.id} className="hover:shadow-lg transition-shadow duration-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{order.id}</h3>
                    <Badge className={`${getStatusColor(order.status)} flex items-center space-x-1`}>
                      {getStatusIcon(order.status)}
                      <span className="capitalize">{order.status}</span>
                    </Badge>
                  </div>
                  
                  <h4 className="text-xl font-bold text-blue-600 mb-2">{order.trainingTitle}</h4>
                  
                  {/* Trainer Info */}
                  <div className="flex items-center space-x-3 mb-4">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={order.trainer.avatar} alt={order.trainer.name} />
                      <AvatarFallback className="bg-blue-600 text-white text-sm">
                        {order.trainer.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-gray-900">{order.trainer.name}</p>
                      <p className="text-sm text-gray-600">{order.trainer.specialization}</p>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">${order.amount.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">{order.paymentTerms}</div>
                </div>
              </div>

              {/* Order Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Order Date</p>
                    <p className="font-medium">{new Date(order.orderDate).toLocaleDateString()}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Training Date</p>
                    <p className="font-medium">{new Date(order.trainingDate).toLocaleDateString()}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-600">Participants</p>
                    <p className="font-medium">{order.participants} people</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-medium">{order.duration}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-4">
                <p className="text-gray-700">{order.description}</p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Location:</strong> {order.location}
                </p>
              </div>

              {/* Milestones */}
              <div className="mb-4">
                <h5 className="font-medium text-gray-900 mb-2">Progress Milestones</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                  {order.milestones.map((milestone, index) => (
                    <div key={index} className="text-center p-2 rounded border">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMilestoneStatusColor(milestone.status)}`}>
                        {milestone.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {milestone.status === 'in-progress' && <Clock className="w-3 h-3 mr-1" />}
                        {milestone.status === 'pending' && <AlertCircle className="w-3 h-3 mr-1" />}
                        {milestone.status === 'cancelled' && <AlertCircle className="w-3 h-3 mr-1" />}
                        {milestone.status.replace('-', ' ')}
                      </div>
                      <p className="text-sm font-medium text-gray-900 mt-1">{milestone.name}</p>
                      <p className="text-xs text-gray-600">{new Date(milestone.date).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(order)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPO(order)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PO
                  </Button>
                </div>
                
                <div className="text-sm text-gray-600">
                  Created on {new Date(order.orderDate).toLocaleDateString()}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredOrders.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No purchase orders found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || filterStatus !== 'all' 
                ? "Try adjusting your search or filter criteria"
                : "You haven't created any purchase orders yet. Start by posting a training requirement."
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <Button className="bg-blue-600 hover:bg-blue-700">
                Post Training Requirement
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Order Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-600">
                {purchaseOrders.filter(o => o.status === 'draft').length}
              </div>
              <div className="text-sm text-gray-800">Draft</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {purchaseOrders.filter(o => o.status === 'pending').length}
              </div>
              <div className="text-sm text-yellow-800">Pending</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {purchaseOrders.filter(o => o.status === 'approved').length}
              </div>
              <div className="text-sm text-blue-800">Approved</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {purchaseOrders.filter(o => o.status === 'completed').length}
              </div>
              <div className="text-sm text-green-800">Completed</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">
                {purchaseOrders.filter(o => o.status === 'cancelled').length}
              </div>
              <div className="text-sm text-red-800">Cancelled</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseOrders;